import type { Type } from '@angular/core';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
} from '@angular/core';

import { DynamicModule } from 'ng-dynamic-component';

import { FeatureSplit as FeatureSplitComponent } from '@valk-nx/components/ui-feature-split/src/lib/feature-split';
import type { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { AnchorComponent } from '@valk-nx/storyblok/components/anchor/src/lib/anchor';
import { HeadingGroupSectionComponent } from '@valk-nx/storyblok/components/heading-group-section/src/lib/heading-group-section';
import { LinkListComponent as SbLinkListComponent } from '@valk-nx/storyblok/components/link-list/src/lib/link-list';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type {
	FeatureSplit,
	HeadingGroupSection,
	SbBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

const Components: Record<string, Type<unknown>> = {
	button: AnchorComponent,
	headingGroupSection: HeadingGroupSectionComponent,
	linkList: SbLinkListComponent,
	richText: RichTextComponent,
	simpleButton: AnchorComponent,
};

const enforcedHeading = {
	alignment: 'left',
	size: 'lg',
} as const;

const fallbackHeadingType: HeadingType = 'h2';

const anchorComponents = new Set(['button', 'simpleButton']);

// The call to action lives in its own field; the legacy content field held it before
const callToActionComponents = new Set([...anchorComponents, 'linkList']);

const underlined = (blok: SbBlok): SbBlok =>
	anchorComponents.has(blok.component)
		? { ...blok, variant: 'underline' }
		: blok;

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sb-feature-split-section',
	templateUrl: './feature-split-section.html',
	imports: [DynamicModule, FeatureSplitComponent, StoryblokDirective],
})
export class StoryblokFeatureSplitComponent extends StoryblokRootDirective {
	components: typeof Components = Components;
	featureSplits = input.required<FeatureSplit[]>();
	startInversed = input(false);

	splits = computed(() =>
		this.featureSplits().map((featureSplit) => ({
			...featureSplit,
			button: this.mapButton(featureSplit),
			content: this.mapContent(featureSplit),
			heading: this.mapHeading(featureSplit),
			photos: this.mapPhotos(featureSplit),
		})),
	);

	private mapButton(featureSplit: FeatureSplit): SbBlok[] {
		return Array.isArray(featureSplit.button)
			? featureSplit.button.map(underlined)
			: [];
	}

	// The legacy content field keeps its own call to action only while the new one is empty
	private mapContent(featureSplit: FeatureSplit): SbBlok[] {
		const content = Array.isArray(featureSplit.content)
			? featureSplit.content
			: [];
		const bloks = this.mapButton(featureSplit).length
			? content.filter(
					(blok) => !callToActionComponents.has(blok.component),
				)
			: content;

		return bloks.map(underlined);
	}

	private mapHeading(featureSplit: FeatureSplit): HeadingGroupSection[] {
		const heading = Array.isArray(featureSplit.heading)
			? featureSplit.heading
			: [];
		const source = heading.length
			? heading
			: this.legacyHeading(featureSplit);

		return source.map((blok) => ({ ...blok, ...enforcedHeading }));
	}

	// Stories keep their own overline and title until an editor fills the heading blok
	// TODO: https://new-story.atlassian.net/browse/VPL-49783 - remove this legacy code.
	private legacyHeading(featureSplit: FeatureSplit): HeadingGroupSection[] {
		if (!featureSplit.title) {
			return [];
		}

		return [
			{
				_uid: featureSplit._uid,
				component: 'headingGroupSection',
				content: '',
				headingType: fallbackHeadingType,
				overline: featureSplit.overline ?? '',
				title: featureSplit.title,
			},
		];
	}

	private mapPhotos(featureSplit: FeatureSplit): PhotoInterface[] {
		if (!featureSplit.images?.length) {
			return [{ src: '' }];
		}

		return featureSplit.images.map((image) =>
			image.bynder?.length
				? StoryblokHelper.mapBynderToPhotoInterface(
						image.bynder[0],
						image.imageAlt,
					)
				: { src: '' },
		);
	}
}
